import {
  Origin,
  Preset,
  SourcePreset,
  type AuthzGrant,
  type AuthzOrgRole,
  type AuthzRoleDefinition,
  type AuthzRoleRef,
} from '@/api'
import { isPresetId, type Catalogue, type PresetId } from './catalogue'
import type { AccessGrant } from './grants'
import type { ActionSets } from './matrix'
import { systemRoles, type CustomCapacity, type RoleDef } from './resolve'

// Passage entre les routes `/organizations/{oid}/authz/*` de la gateway et le modèle de l'écran. Côté
// API, un rôle de l'organisation porte ses capacités Nabu suivies et ses copies (capacité → une partie
// de ses actions) ; côté écran, une copie est une capacité personnalisée que le rôle référence.

/** L'état « rôles et accès » d'une organisation, tel que l'écran le lit. */
export interface AuthzState {
  roles: RoleDef[]
  customCapacities: CustomCapacity[]
  grants: AccessGrant[]
}

const PRESET: Record<PresetId, Preset> = {
  guest: Preset.Guest,
  declarant: Preset.Declarant,
  manager: Preset.Manager,
  admin: Preset.Admin,
}

const SOURCE_PRESET: Record<PresetId, SourcePreset> = {
  guest: SourcePreset.Guest,
  declarant: SourcePreset.Declarant,
  manager: SourcePreset.Manager,
  admin: SourcePreset.Admin,
}

/** Une copie n'a pas d'identifiant côté API : c'est la capacité Nabu qu'elle réduit, dans un rôle. */
export const copyIdOf = (roleId: string, capacityId: string): string =>
  `${roleId}/${capacityId}`

const orgRoleFromApi = (
  catalogue: Catalogue,
  role: AuthzOrgRole
): { role: RoleDef; copies: CustomCapacity[] } => {
  const followed = new Set(role.capacities)
  const copies: CustomCapacity[] = []
  const capacities: string[] = []
  for (const capacity of catalogue.capacities) {
    const copied = role.capacityCopies[capacity.id]
    if (followed.has(capacity.id)) capacities.push(capacity.id)
    else if (copied) {
      const id = copyIdOf(role.id, capacity.id)
      copies.push({ id, sourceCapacityId: capacity.id, actions: [...copied] })
      capacities.push(id)
    }
  }
  return {
    role: {
      id: role.id,
      name: role.name,
      origin: 'custom',
      sourcePresetId: isPresetId(role.sourcePreset)
        ? role.sourcePreset
        : undefined,
      capacities,
      createdAt: role.createdAt,
    },
    copies,
  }
}

export const grantFromApi = (grant: AuthzGrant): AccessGrant => ({
  id: grant.id,
  userId: grant.userId,
  roleId: grant.roleId ?? grant.preset ?? '',
  groupId: grant.groupId,
  includeDescendants: grant.includeDescendants,
  validFrom: grant.validFrom,
  validUntil: grant.validUntil,
  createdAt: grant.createdAt,
  origin: grant.origin === Origin.Migration ? 'migration' : 'manual',
})

/** Les rôles Nabu du catalogue, puis ceux de l'organisation avec leurs copies de capacités. */
export const rolesFromApi = (
  catalogue: Catalogue,
  orgRoles: readonly AuthzOrgRole[]
): Pick<AuthzState, 'roles' | 'customCapacities'> => {
  const mapped = orgRoles.map((role) => orgRoleFromApi(catalogue, role))
  return {
    roles: [...systemRoles(catalogue), ...mapped.map((m) => m.role)],
    customCapacities: mapped.flatMap((m) => m.copies),
  }
}

/** Le rôle d'un accès : un rôle Nabu par son préréglage, un rôle de l'organisation par son id. */
export const roleRefOf = (roleId: string): AuthzRoleRef =>
  isPresetId(roleId) ? { preset: PRESET[roleId] } : { roleId }

/**
 * Les cases cochées d'un rôle, capacité par capacité : toutes → la capacité Nabu, qui suit ce que Nabu
 * y met ; une partie → une copie propre au rôle, qui ne suit plus Nabu ; aucune → hors du rôle.
 */
export const roleContentOf = (
  catalogue: Catalogue,
  sets: ActionSets
): Pick<AuthzRoleDefinition, 'capacities' | 'capacityCopies'> => {
  const capacities: string[] = []
  const capacityCopies: Record<string, string[]> = {}
  for (const capacity of catalogue.capacities) {
    const set = sets.get(capacity.id)
    if (!set?.size) continue
    const kept = capacity.actions.filter((code) => set.has(code))
    if (kept.length === capacity.actions.length) capacities.push(capacity.id)
    else if (kept.length) capacityCopies[capacity.id] = kept
  }
  return { capacities, capacityCopies }
}

/**
 * Les rôles et accès tels qu'ils seront une fois ce rôle enregistré avec ces cases : de quoi simuler
 * l'enregistrement avant de le faire (TEC-1678). Les copies du rôle sont refaites, comme à la relecture.
 */
export const stateWithRoleContent = (
  catalogue: Catalogue,
  state: AuthzState,
  roleId: string,
  sets: ActionSets
): AuthzState => {
  const content = roleContentOf(catalogue, sets)
  const copies = Object.entries(content.capacityCopies).map(([capacityId, actions]) => ({
    id: copyIdOf(roleId, capacityId),
    sourceCapacityId: capacityId,
    actions: [...actions],
  }))
  return {
    ...state,
    roles: state.roles.map((role) =>
      role.id === roleId
        ? { ...role, capacities: [...content.capacities, ...copies.map((copy) => copy.id)] }
        : role
    ),
    customCapacities: [
      ...state.customCapacities.filter((copy) => !copy.id.startsWith(`${roleId}/`)),
      ...copies,
    ],
  }
}

export const roleDefinitionOf = (
  catalogue: Catalogue,
  presetId: PresetId,
  name: string,
  sets: ActionSets
): AuthzRoleDefinition => ({
  name: name.trim(),
  sourcePreset: SOURCE_PRESET[presetId],
  ...roleContentOf(catalogue, sets),
})
